import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FileWarning } from "lucide-react";
import type {
  BankStatementImportLogEntryDto,
  CloudApiLogEntryDto,
  CourierApiLogEntryDto,
  FiscalizationLogEntryDto,
  MobileLogEntryDto,
  PfrResponseLogEntryDto,
  SefApiLogEntryDto,
} from "@finora/types";
import { EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

type TabKey = "fiscalization" | "cloud-api" | "statements" | "mobile" | "pfr" | "couriers" | "sef";

const TABS: { key: TabKey; label: string }[] = [
  { key: "fiscalization", label: "Fiskalizacija" },
  { key: "cloud-api", label: "Cloud API" },
  { key: "statements", label: "Uvoz izvoda" },
  { key: "mobile", label: "Mobile" },
  { key: "pfr", label: "PFR odgovori" },
  { key: "couriers", label: "Kuriri" },
  { key: "sef", label: "SEF" },
];

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("sr-RS");
}

function FiscalizationTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-fiscalization"],
    queryFn: () => api.listFiscalizationLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Nema zabeleženih fiskalizacija" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Broj (PFR)</th>
          <th>Klijent</th>
          <th>Datum</th>
          <th>Iznos</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: FiscalizationLogEntryDto) => {
          const failed = row.status === "FISCALIZATION_FAILED";
          const ok = row.status === "FISCALIZED";
          return (
            <tr key={row.id}>
              <td className="font-medium text-slate-800">
                {row.fiscalNumber ?? row.documentNumber ?? "—"}
              </td>
              <td className="text-slate-600">{row.companyName}</td>
              <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
              <td className="text-slate-500">
                {row.grossAmount} {row.currency}
              </td>
              <td>
                <StatusBadge
                  label={row.status}
                  tone={ok ? "success" : failed ? "error" : "neutral"}
                />
                {failed && row.lastErrorMessage && (
                  <p className="mt-0.5 text-xs text-red-500">{row.lastErrorMessage}</p>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CloudApiTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-cloud-api"],
    queryFn: () => api.listCloudApiLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Još nema zabeleženih Cloud API zahteva" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Vreme</th>
          <th>ID</th>
          <th>Klijent</th>
          <th>API URL</th>
          <th>IP</th>
          <th>Status</th>
          <th>Opis greške</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: CloudApiLogEntryDto) => {
          const isError = row.statusCode >= 400;
          return (
            <tr key={row.id}>
              <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
              <td className="font-mono text-xs text-slate-500">{row.id}</td>
              <td className="text-slate-600">{row.companyName ?? "Nepoznato"}</td>
              <td className="text-slate-800">
                <span className="mr-1 font-mono text-xs text-slate-400">{row.method}</span>
                {row.path}
              </td>
              <td className="font-mono text-xs text-slate-500">{row.ip ?? "—"}</td>
              <td>
                <span className={isError ? "text-red-500" : "text-emerald-600"}>{row.statusCode}</span>
              </td>
              <td className="text-slate-500">
                {isError && row.errorMessage ? row.errorMessage : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function StatementsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-statements"],
    queryFn: () => api.listBankStatementImportLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Nema zabeleženih uvoza izvoda" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Klijent</th>
          <th>PIB</th>
          <th>Fajl</th>
          <th>Banka</th>
          <th>Broj izvoda</th>
          <th>Status</th>
          <th>Greška</th>
          <th>Datum</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: BankStatementImportLogEntryDto) => {
          const failed = row.status === "FAILED";
          return (
            <tr key={row.id}>
              <td className="text-slate-600">{row.companyName ?? "Nepoznato"}</td>
              <td className="text-slate-500">{row.pib}</td>
              <td className="text-slate-500">{row.filename ?? "—"}</td>
              <td className="text-slate-500">{row.bank ?? "—"}</td>
              <td className="text-slate-500">{row.statementNumber ?? "—"}</td>
              <td>
                <StatusBadge label={row.status} tone={failed ? "error" : "success"} />
              </td>
              <td className="text-red-500">
                {failed ? (row.errorTitle ?? row.errorMessage ?? row.errorCode ?? "") : ""}
              </td>
              <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MobileTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-mobile"],
    queryFn: () => api.listMobileLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Još nema zabeleženih Mobile API zahteva" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Vreme</th>
          <th>Metoda</th>
          <th>Putanja</th>
          <th>IP</th>
          <th>Status</th>
          <th>Greška</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: MobileLogEntryDto) => {
          const isError = (row.statusCode ?? 0) >= 400;
          return (
            <tr key={row.id}>
              <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
              <td className="font-mono text-xs text-slate-400">{row.method}</td>
              <td className="text-slate-800">{row.path}</td>
              <td className="font-mono text-xs text-slate-500">{row.ip ?? "—"}</td>
              <td>
                <span className={isError ? "text-red-500" : "text-emerald-600"}>
                  {row.statusCode ?? "—"}
                </span>
              </td>
              <td className="text-slate-500">
                {isError && row.errorMessage ? row.errorMessage : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PfrResponseTab() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-pfr"],
    queryFn: () => api.listPfrLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Još nema zabeleženih PFR poziva" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Vreme</th>
          <th>Račun</th>
          <th>Operacija</th>
          <th>Status</th>
          <th>Request ID</th>
          <th>Odgovor</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: PfrResponseLogEntryDto) => {
          const expanded = expandedId === row.id;
          const rawText = row.errorMessage ?? row.rawResponse ?? "";
          const shortText = rawText.length > 80 ? `${rawText.slice(0, 80)}…` : rawText;
          return (
            <tr key={row.id}>
              <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
              <td className="font-mono text-xs text-slate-500">{row.fiscalDocumentId ?? "—"}</td>
              <td className="text-slate-800">{row.operation}</td>
              <td>
                <StatusBadge
                  label={row.success ? "USPEH" : "GREŠKA"}
                  tone={row.success ? "success" : "error"}
                />
                {row.statusCode != null && (
                  <span className="ml-1 font-mono text-xs text-slate-400">{row.statusCode}</span>
                )}
              </td>
              <td className="font-mono text-xs text-slate-500">{row.requestId ?? "—"}</td>
              <td className="max-w-xs text-slate-500">
                {rawText ? (
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    className="text-left underline decoration-dotted"
                  >
                    {expanded ? rawText : shortText}
                  </button>
                ) : (
                  ""
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CouriersTab() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-couriers"],
    queryFn: () => api.listCourierLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Još nema zabeleženih poziva ka kuririma" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Vreme</th>
          <th>Klijent</th>
          <th>Kurir</th>
          <th>Operacija</th>
          <th>Status</th>
          <th>Detalji</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: CourierApiLogEntryDto) => {
          const expanded = expandedId === row.id;
          const text = row.errorMessage ?? row.response ?? "";
          const short = text.length > 80 ? `${text.slice(0, 80)}…` : text;
          return (
            <>
              <tr key={row.id}>
                <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
                <td className="text-slate-600">{row.companyName ?? "—"}</td>
                <td className="text-slate-800">{row.courier}</td>
                <td className="text-slate-800">{row.operation}</td>
                <td>
                  <StatusBadge label={row.success ? "USPEH" : "GREŠKA"} tone={row.success ? "success" : "error"} />
                </td>
                <td className="max-w-xs text-slate-500">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    className="text-left underline decoration-dotted"
                  >
                    {short || "prikaži"}
                  </button>
                </td>
              </tr>
              {expanded && (
                <tr key={`${row.id}-d`}>
                  <td colSpan={6} className="bg-slate-50 text-xs text-slate-600">
                    <div className="font-semibold">Zahtev</div>
                    <pre className="whitespace-pre-wrap break-all">{row.request ?? "—"}</pre>
                    <div className="mt-2 font-semibold">{row.success ? "Odgovor" : "Greška"}</div>
                    <pre className="whitespace-pre-wrap break-all">{row.errorMessage ?? row.response ?? "—"}</pre>
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </table>
  );
}

function SefTab() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-logs-sef"],
    queryFn: () => api.listSefLogsAdmin({ limit: 200 }),
  });

  if (isLoading) return <LoadingRows />;
  if (!data || data.length === 0) {
    return <EmptyState icon={FileWarning} title="Još nema zabeleženih poziva ka SEF-u" description="" />;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Vreme</th>
          <th>Klijent</th>
          <th>Okruženje</th>
          <th>Operacija</th>
          <th>Status</th>
          <th>Detalji</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row: SefApiLogEntryDto) => {
          const expanded = expandedId === row.id;
          const text = row.errorMessage ?? row.response ?? "";
          const short = text.length > 80 ? `${text.slice(0, 80)}…` : text;
          return (
            <>
              <tr key={row.id}>
                <td className="text-slate-500">{formatDateTime(row.createdAt)}</td>
                <td className="text-slate-600">{row.companyName ?? "—"}</td>
                <td className="text-slate-600">{row.mode === "PROD" ? "Produkcija" : "Demo"}</td>
                <td className="text-slate-800">{row.operation}</td>
                <td>
                  <StatusBadge
                    label={row.success ? "USPEH" : `GREŠKA${row.httpStatus ? ` ${row.httpStatus}` : ""}`}
                    tone={row.success ? "success" : "error"}
                  />
                </td>
                <td className="max-w-xs text-slate-500">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : row.id)}
                    className="text-left underline decoration-dotted"
                  >
                    {short || "prikaži"}
                  </button>
                </td>
              </tr>
              {expanded && (
                <tr key={`${row.id}-d`}>
                  <td colSpan={6} className="bg-slate-50 text-xs text-slate-600">
                    <div className="font-semibold">Zahtev</div>
                    <pre className="whitespace-pre-wrap break-all">{row.request ?? "—"}</pre>
                    <div className="mt-2 font-semibold">Greška</div>
                    <pre className="whitespace-pre-wrap break-all">{row.errorMessage ?? "—"}</pre>
                    <div className="mt-2 font-semibold">Odgovor SEF-a</div>
                    <pre className="whitespace-pre-wrap break-all">{row.response ?? "—"}</pre>
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </table>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-2 p-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

export function LogsPage() {
  const [tab, setTab] = useState<TabKey>("fiscalization");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Logovi</h1>
        <p className="text-sm text-slate-500">
          Fiskalizacija, Cloud API, uvoz izvoda, Mobile i PFR odgovori preko svih firmi na platformi
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? "bg-blue-600 text-white"
                : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {tab === "fiscalization" && <FiscalizationTab />}
        {tab === "cloud-api" && <CloudApiTab />}
        {tab === "statements" && <StatementsTab />}
        {tab === "mobile" && <MobileTab />}
        {tab === "pfr" && <PfrResponseTab />}
        {tab === "couriers" && <CouriersTab />}
        {tab === "sef" && <SefTab />}
      </div>
    </div>
  );
}
